import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger, MotionPathPlugin } from 'gsap/all';
import SplitType from 'split-type';
import LocomotiveScroll from 'locomotive-scroll';
import 'locomotive-scroll/dist/locomotive-scroll.css';
import { Bounce } from 'gsap/all';
import $ from 'jquery';
import Swiper from 'swiper';
import 'swiper/swiper-bundle.css';
import { Flip } from 'gsap/Flip';
import { Autoplay, Navigation } from 'swiper/modules';
import swal from 'sweetalert';
import 'jquery-validation';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin, Bounce, Flip);

const Preloader = () => {
    let locoScroll = null; // Declare locoScroll at component level

    useEffect(() => {

        // Load Textify.js from CDN
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/textify.js/dist/Textify.min.js';
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => {
            const preloader = document.querySelector(".preloader");
            if (!preloader) {
                // Non-home pages (e.g. privacy policy) — allow native scrolling
                document.body.style.overflow = 'visible';
                document.body.style.overflowX = 'hidden';
                return;
            }

            const progressBar = document.querySelector(".progress-bar");
            const percentageText = document.querySelector(".percentage");
            const loadingText = new SplitType(".loading-text.initial", { types: "chars" });
            const completeText = new SplitType(".loading-text.complete", { types: "chars" });
            const titleText = new SplitType(".content h1", { types: "chars" });
            const paragraphText = new SplitType(".content p", { types: "chars" });

            const images = document.images;
            let loadedImages = 0;
            const totalImages = images.length;

            gsap.set(".loading-text.complete", { y: "100%" });
            gsap.set(loadingText.chars, { opacity: 0, y: 100 });
            gsap.set(completeText.chars, { opacity: 0, y: 100 });

            const paths = document.querySelectorAll('.progress-icon path');

            function updatePathAnimation(progress) {
                paths.forEach((path) => {
                    gsap.to(path, {
                        duration: 0.5,
                        opacity: progress / 100,
                        scale: progress / 100,
                        rotation: progress * 3.6,
                        ease: "power2.out"
                    });
                });
            }

            const colorStages = [
                { bg: "#243CE6", text: "rgb(255, 255, 255)" },
                { bg: "#243CE6", text: "rgb(30, 30, 30)" },
                { bg: "#243CE6", text: "rgb(255, 255, 255)" },
                { bg: "#243CE6", text: "rgb(255, 255, 255)" }
            ];

            function updateColors(progress) {
                const stage = Math.floor(progress / 25);
                if (stage < colorStages.length) {
                    preloader.style.backgroundColor = colorStages[stage].bg;
                    progressBar.style.backgroundColor = colorStages[stage].text;
                    document.querySelectorAll(".loading-text .char, .percentage").forEach((el) => {
                        el.style.color = colorStages[stage].text;
                    });
                }
            }

            function imageLoaded() {
                loadedImages += 1;
                const progress = Math.floor((loadedImages / totalImages) * 100);
                progressBar.style.width = `${progress}%`;
                percentageText.textContent = progress;
                updateColors(progress);
                updatePathAnimation(progress);
                if (loadedImages === totalImages) doneLoading();
            }

            function doneLoading() {
                const tl = gsap.timeline();
                tl.to(".loading-text.initial", {
                    y: "-100%",
                    duration: 0.5,
                    ease: "power2.inOut"
                })
                    .to(".loading-text.complete", {
                        y: "0%",
                        duration: 0.5,
                        ease: "power2.inOut",
                        opacity: 1
                    }, "<")
                    .to(completeText.chars, {
                        opacity: 1,
                        y: 0,
                        duration: 0.3,
                        stagger: 0.03,
                        ease: "power2.out"
                    }, "<0.2")
                    .to(".preloader", {
                        y: "-100vh",
                        duration: 1,
                        ease: "power2.inOut",
                        delay: 0.8,
                        onComplete: () => {
                            if (window.innerWidth > 768) {
                                loadLogoAnimation(); // optional
                            } else {
                                document.body.style.overflow = 'visible';
                                document.body.style.overflowX = 'hidden';
                            }
                        }
                    })
                    .to([titleText.chars, paragraphText.chars], {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        stagger: 0.02,
                        ease: "power4.out"
                    }, "-=0.5")
                    .set(".preloader", { display: "none" });

                if (window.locoScroll) window.locoScroll.update?.();
                animationAfterLoading(); // optional
            }

            for (let i = 0; i < totalImages; i++) {
                const img = new Image();
                img.onload = imageLoaded;
                img.onerror = imageLoaded;
                img.src = images[i].src;
            }

            // Wait for DOM to be ready before initializing Locomotive Scroll
            const initLocomotiveScroll = () => {
                const smoothScrollElement = document.querySelector(".smooth-scroll");
                if (!smoothScrollElement) {
                    console.warn("Smooth scroll element not found, retrying...");
                    setTimeout(initLocomotiveScroll, 100);
                    return;
                }

                locoScroll = new LocomotiveScroll({
                    el: smoothScrollElement,
                    smooth: true,
                    defaultDirection: 'top'
                });

                locoScroll.on("scroll", ScrollTrigger.update);

                if (window.innerWidth > 768) {
                    ScrollTrigger.scrollerProxy(".smooth-scroll", {
                        scrollTop(value) {
                            return arguments.length ? locoScroll.scrollTo(value, 0, 0) : locoScroll.scroll.instance.scroll.y;
                        },
                        getBoundingClientRect() {
                            return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
                        },
                        pinType: smoothScrollElement.style.transform ? "transform" : "fixed"
                    });
                }

                window.locoScroll = locoScroll;
            };

            // Initialize Locomotive Scroll with delay
            setTimeout(initLocomotiveScroll, 500);



            function animationAfterLoading() {
                gsap.to(".home-section-1", {
                    backgroundPosition: "20% -80%", // Move the background vertically
                    duration: 3,
                    ease: "power1.inOut",
                    repeat: -1,  // Infinite loop
                    yoyo: true   // Reverse the animation
                });
                gsap.from(".arrow-div1,.arrow-div2,.arrow-div3", {
                    opacity: 0,
                    duration: 1.5,
                    x: -50,
                    ease: "power4.out",
                    delay: 3,
                    stagger: 0.2,
                    // scrollTrigger: {
                    //     trigger: ".content-1-3",
                    //     start: "top 85%",
                    //     scroller: ".smooth-scroll",
                    //     scrub: true,
                    //     toggleActions: "play none none none",
                    //     once: true
                    // }
                });
                if (window.innerWidth > 768) {
                    new window.Textify({
                        el: '.textify1-1',
                        animation: {
                            stagger: 0.025,
                            duration: 0.7,
                            ease: 'expo.inOut',
                            delay: 1.8,
                            animateProps: {
                                "opacity": 0,
                                "scale": 0,
                                "x": -100,
                                "rotate": 180,

                            }
                        }
                    }, gsap)
                }
                if (window.innerWidth > 768) {
                    new window.Textify({
                        el: '.textify1-2',
                        animation: {
                            stagger: 0.025,
                            duration: 0.7,
                            ease: 'expo.inOut',
                            delay: 2.5,
                            animateProps: {
                                "opacity": 0,
                                "scale": 0,
                                "x": -100,
                                "rotate": 180,

                            }
                        }
                    }, gsap)
                }
                if (window.innerWidth > 768) {
                    new window.Textify({
                        el: '.textify1-3',
                        animation: {
                            stagger: 0.025,
                            duration: 0.7,
                            ease: 'expo.inOut',
                            delay: 2.9,
                            animateProps: {
                                "opacity": 0,
                                "scale": 0,
                                "x": -100,
                                "rotate": 180,

                            }
                        }
                    }, gsap)
                }
                if (window.innerWidth > 768) {
                    gsap.from(".fade-right-3", {
                        delay: 2.9,
                        opacity: 0,
                        scrub: 1,
                        duration: 3, ease: "elastic.out(1, 0.3)", x: -70
                    });
                }
                if (window.innerWidth > 768) {
                    gsap.from(".fade-right-4", {
                        delay: 1.8,
                        opacity: 0,
                        scrub: 1,
                        duration: 3, ease: "elastic.out(1, 0.3)", x: -70
                    });
                }

                gsap.set('.Ellipse-1-img,.Ellipse-2-img', { y: '-130px' });


                gsap.to('.Ellipse-1-img,.Ellipse-2-img', 2, {
                    y: '1px',
                    ease: 'bounce.easeOut',
                    yoyo: true,
                    repeat: -1,
                    repeatDelay: 0,
                    onStart: function () {

                    }
                }, 0.2);

                gsap.set('.round-ball', { x: '-200px' });


                gsap.to('.round-ball', 2, {
                    x: '1px',
                    ease: 'bounce.easeOut',
                    yoyo: true,
                    repeat: -1,
                    repeatDelay: .5,
                    onStart: function () {

                    }
                }, 0.2);



                gsap.to(".program21", {
                    rotation: 360,
                    duration: 10,
                    ease: "power2.inOut",
                    repeat: -1,
                    ease: "none"
                });

                gsap.to(".hand-in-circle", {
                    rotation: -360,
                    duration: 10,
                    ease: "power2.inOut",
                    repeat: -1,
                    ease: "none"
                });

                if (window.innerWidth > 768) {
                    gsap.from(".big-text-main ", {
                        scrollTrigger: {
                            trigger: ".big-text-main",
                            scroller: ".smooth-scroll",
                            start: "top 100%",
                            end: "top 100%",
                            scrub: true,
                            onLeave: () => {
                                startTextifyAnimation1();
                                startTextifyAnimation2();
                                startTextifyAnimation3();
                            }
                        },
                        duration: .1,
                    });
                }

                gsap.to(".big-text,.card-1 .para-1,.card-1 .para-2,.card-2 p,.card-3 p,card-4 p,.live-animation1", {
                    y: -10, // Move the image up by 10px for a small jump
                    duration: 1, // Shorter duration for a quick jump (0.5 seconds)
                    ease: "power1.inOut", // Smooth easing for a subtle effect
                    repeat: -1, // Repeat infinitely
                    yoyo: true // Return to the original position
                });

                function startTextifyAnimation1() {
                    new window.Textify({
                        el: '.big-text-main,.form-section h3',
                        animation: {
                            stagger: 0.025,
                            duration: 0.7,
                            ease: 'expo.inOut',
                            animateProps: { "opacity": 0, "scale": 0 }
                        }
                    }, gsap)
                }

                function startTextifyAnimation2() {
                    new window.Textify({
                        el: '.textify-1 .down-div',
                        animation: {
                            stagger: 0.05,
                            duration: 0.7,
                            ease: 'expo.inOut',
                            animateProps: { "y": "-100%", "opacity": 0, "skewX": -45 }
                        }
                    }, gsap)
                }

                function startTextifyAnimation3() {
                    new window.Textify({
                        el: '.textify-1 .up-div',
                        splitType: 'lines',
                        largeText: true,
                        animation: {
                            by: 'lines',
                            stagger: 0.075,
                            duration: 0.7,
                            ease: 'power2',
                            transformOrigin: 'center center',
                            animateProps: { "scale": 0, "opacity": 0 }
                        }
                    }, gsap)
                }

                gsap.from(".content-1-3 ul li", {
                    scrollTrigger: {
                        trigger: ".content-1-3",
                        scroller: ".smooth-scroll",
                        start: "top 100%",
                        end: "bottom 50%",
                        scrub: true,
                        toggleActions: "restart none none none",
                    },
                    delay: .3,
                    opacity: 0,
                    duration: 2.5,
                    x: 90,
                    scale: .2,
                    stagger: 0.3
                });

                // gsap.to(".flower-img", {
                //     scale: 1,
                //     rotation: 360,
                //     duration: 5,
                //     ease: "power2.inOut",
                //     repeat: -1,
                //     yoyo: true
                // });

                gsap.to(".graphics-element img", {
                    duration: 1,                  // Duration of each cycle
                    y: "-50px",                   // Moves image upward
                    ease: "bounce.out",           // Bounce easing for a playful effect
                    repeat: -1,                   // Infinite loop
                    yoyo: true,                   // Reverse the bounce after each cycle
                    repeatDelay: 0.5,             // Delay before the repeat starts
                    delay: 0.3,                   // Slight delay before the first cycle
                });



                gsap.from(".donate-btn", {
                    y: 60,
                    opacity: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    delay: 0.5,
                    stagger: 0.2,
                    scrollTrigger: {
                        trigger: ".content-1-4",
                        scroller: ".smooth-scroll",
                        start: "top 100%",
                        scrub: true,
                        toggleActions: "play none none none",
                    },
                });
                gsap.from(".call-btn", {
                    y: 60,
                    opacity: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    delay: 0.5,
                    stagger: 0.2,
                    scrollTrigger: {
                        trigger: ".content-1-5",
                        scroller: ".smooth-scroll",
                        start: "top 100%",
                        scrub: true,
                        toggleActions: "play none none none",
                    },
                });
                gsap.from(".arrow-btn", {
                    y: 60,
                    opacity: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    delay: 0.5,
                    stagger: 0.2,
                    scrollTrigger: {
                        trigger: ".content-1-5",
                        scroller: ".smooth-scroll",
                        start: "top 100%",
                        scrub: true,
                        toggleActions: "play none none none",
                    },
                });
                // Contact Info Section
                if (window.innerWidth > 768) {
                    gsap.from(".contact-info .contact-item", {
                        x: -50,
                        opacity: 0,
                        duration: 1,
                        ease: "power3.out",
                        delay: 0.5,
                        stagger: 0.3,
                        scrollTrigger: {
                            trigger: ".contact-info",
                            scroller: ".smooth-scroll",
                            start: "top 100%",
                            end: "bottom 60%",
                            scrub: true,
                            toggleActions: "play none none none",
                        },
                        onComplete: function () {
                            if (locoScroll && typeof locoScroll.update === 'function') {
                                locoScroll.update();
                            }
                        }
                    });
                }
                if (window.innerWidth > 768) {
                    gsap.from(".button-sec .btn", {
                        y: 50,
                        opacity: 0,
                        duration: 1.5,
                        ease: "power3.out",
                        delay: 0.5,
                        scrollTrigger: {
                            trigger: ".button-sec .btn",
                            scroller: ".smooth-scroll",
                            start: "top 80%",
                            scrub: true,
                            toggleActions: "play none none none",
                        },
                    });
                }

                if (window.innerWidth > 768) {
                    // gsap.from(".form-inputs .form-control", {
                    //     x: -50,
                    //     opacity: 0,
                    //     duration: 1,
                    //     ease: "power3.out",
                    //     delay: 0.5,
                    //     stagger: 0.3,
                    //     scrollTrigger: {
                    //         trigger: ".contact-main-section",
                    //         scroller: ".smooth-scroll",
                    //         start: "top 100%",
                    //         end: "bottom 70%",
                    //         scrub: true,
                    //         onLeave: () => {
                    //             startTextifyAnimation1();
                    //         },
                    //         toggleActions: "play none none none",
                    //     },
                    //     onComplete: function () {
                    //         locoScroll.update();
                    //     }
                    // });
                }

                // if (window.innerWidth > 768) {
                //   gsap.from(".head-section", {
                //     scrollTrigger: {
                //       trigger: ".head-section",
                //       scroller: ".smooth-scroll",
                //       start: "top 100%",
                //       end: "top 100%",
                //       scrub: true,
                //       onLeave: () => {
                //         startTextifyAnimation10();
                //         startTextifyAnimation11();
                //       }
                //     },
                //     duration: .1,
                //   });
                // }
                // function startTextifyAnimation10() {
                //   new window.Textify({
                //     el: '.form-section h2',
                //     animation: {
                //       stagger: 0.025,
                //       duration: 0.7,
                //       ease: 'expo.inOut',
                //       animateProps: { "opacity": 0, "scale": 0 }
                //     }
                //   }, gsap)
                // }
                // function startTextifyAnimation11() {
                //   new window.Textify({
                //     el: '.form-section p',
                //     animation: {
                //       stagger: 0.05,
                //       duration: 0.7,
                //       ease: 'expo.inOut',
                //       animateProps: { "y": "-100%", "opacity": 0, "skewX": -45 }
                //     }
                //   }, gsap)
                // }

                if (window.innerWidth > 768) {
                    gsap.from(".h-div ", {
                        scrollTrigger: {
                            trigger: ".h-div",
                            scroller: ".smooth-scroll",
                            start: "top 100%",
                            end: "top 100%",
                            scrub: true,
                            onLeave: () => {
                                startTextifyAnimation1();
                                startTextifyAnimation2();
                                startTextifyAnimation3();
                            }
                        },
                        duration: .1,
                    });

                    // gsap.to(".gallery-section img", {
                    //   y: -10,
                    //   duration: 1.5,
                    //   ease: "power1.inOut",
                    //   repeat: -1,
                    //   yoyo: true,
                    //   stagger: 0.3
                    // });
                    if (window.innerWidth > 768) {
                        // gsap.from(".section-4 .head-section h2,.date-div", {
                        //     opacity: 0,
                        //     x: -100,
                        //     scale: 0.9,
                        //     duration: 1.2,
                        //     ease: "power3.out",
                        //     scrollTrigger: {
                        //         trigger: ".section-4",
                        //         start: "top 100%",
                        //         scroller: ".smooth-scroll",
                        //         scrub: true,
                        //         toggleActions: "play none none none",
                        //     },
                        // });
                    }
                    if (window.innerWidth > 768) {
                        gsap.from(".section-4 .head-section p,.loc-div", {
                            opacity: 0,
                            x: 100,
                            scale: 0.9,
                            duration: 1.2,
                            ease: "power3.out",
                            scrollTrigger: {
                                trigger: ".section-4",
                                start: "top 100%",
                                scroller: ".smooth-scroll",
                                scrub: true,
                                toggleActions: "play none none none",
                            },
                        });
                    }


                    gsap.from(".content-1-3 .list-div h3", {
                        opacity: 0,
                        x: 100,
                        scale: 0.9,
                        duration: 1.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".content-1-3",
                            start: "top 100%",
                            scroller: ".smooth-scroll",
                            scrub: true,
                            toggleActions: "play none none none",
                        },
                    });
                    // const tl3 = gsap.timeline({ repeat: -1, yoyo: true, repeatDelay: 0.3 });

                    // // Slide-in animation for next and prev buttons
                    // tl3.from(".swiper-button-next", {
                    //   opacity: 0,
                    //   x: -100, // Slide in from the right
                    //   duration: 0.6,
                    //   ease: "power2.out"
                    // })
                    //   .from(".swiper-button-prev", {
                    //     opacity: 0,
                    //     x: 100, // Slide in from the left
                    //     duration: 0.6,
                    //     ease: "power2.out"
                    //   }, "-=0.3");

                }



                if (window.innerWidth > 768) {
                    gsap.from(".carousel-first-section", {
                        scrollTrigger: {
                            trigger: ".carousel-first-section",
                            scroller: ".smooth-scroll",
                            start: "top 100%",
                            end: "top 100%",
                            scrub: true,
                            onLeave: () => {
                                //startTextifyAnimation5();
                                //startTextifyAnimation7();
                            }
                        },
                        duration: .1,
                    });
                }


                function startTextifyAnimation5() {
                    new window.Textify({
                        el: '.carousel-first-section h1',
                        animation: {
                            stagger: 0.025,
                            duration: 0.7,
                            ease: 'expo.inOut',
                            animateProps: { "opacity": 0, "scale": 0 }
                        }
                    }, gsap)
                }


                function startTextifyAnimation7() {
                    new window.Textify({
                        el: '.blog-details p',
                        splitType: 'lines',
                        largeText: true,
                        animation: {
                            by: 'lines',
                            stagger: 0.075,
                            duration: 0.7,
                            ease: 'power2',
                            transformOrigin: 'center center',
                            animateProps: { "scale": 0, "opacity": 0 }
                        }
                    }, gsap)
                }
                gsap.from(".carousel-first-section .first-p-acrousel", {
                    opacity: 0,
                    y: -100,
                    scale: 0.9,
                    duration: 1.2,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".carousel-first-section",
                        start: "top 100%",
                        scroller: ".smooth-scroll",
                        scrub: true,
                        toggleActions: "play none none none",
                    },
                });
                if (window.innerWidth > 768) {
                    gsap.from(".blog-details h3,.blog-details p", {
                        opacity: 0,
                        x: -50,
                        scale: 0.9,
                        duration: 1.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".blog-details",
                            start: "top 100%",
                            scroller: ".smooth-scroll",
                            scrub: true,
                            toggleActions: "play none none none",
                        },
                    });
                }



                // Animation for the second h3 element in para-1 (second child)
                gsap.from(".section-3 .para-1 h3", {
                    rotation: 15, // Add a slight rotation effect to the element
                    opacity: 0,
                    x: -200, // Move in from the left
                    duration: 1.5,
                    ease: "back.out(1.7)", // Add a "pop" effect as it moves in
                    scrollTrigger: {
                        trigger: ".section-3 .para-1",
                        scroller: ".smooth-scroll",
                        start: "top 80%",
                        toggleActions: "restart none none none",
                    },
                });


                gsap.from(".section-3 .para-1 p", {
                    opacity: 0,
                    scale: 0.8, // Slight zoom effect
                    x: -50, // Slide in from left
                    duration: 1.5,
                    delay: 0.3, // Add a slight delay for staggered effect
                    ease: "power3.out", // Smooth transition
                    scrollTrigger: {
                        trigger: ".section-3 .para-1",
                        scroller: ".smooth-scroll",
                        start: "top 80%",
                        toggleActions: "restart none none none",
                    },
                });

                if (window.innerWidth > 768) {
                    const defaultEase = "expo.out"; // Smoother easing
                    const defaultScrub = 0.8; // Smooth scrub effect
                    const defaultDuration = 1.5;

                    // Animate the event heading
                    gsap.from(".event-head h1", {
                        opacity: 0,
                        y: -80,
                        scale: 1.2,
                        duration: defaultDuration,
                        ease: defaultEase,
                        scrollTrigger: {
                            trigger: ".event-head",
                            start: "top 80%",
                            end: "bottom 60%",
                            scroller: ".smooth-scroll",
                            scrub: defaultScrub,
                            toggleActions: "play none none none",
                        },
                    });

                    // Animate the heading description with stagger
                    gsap.from(".event-head p", {
                        opacity: 0,
                        y: 50,
                        scale: 1.1,
                        duration: defaultDuration,
                        ease: "circ.out",
                        stagger: 0.2, // Stagger effect
                        scrollTrigger: {
                            trigger: ".event-head",
                            start: "top 80%",
                            end: "bottom 60%",
                            scroller: ".smooth-scroll",
                            scrub: defaultScrub,
                            toggleActions: "play none none none",
                        },
                    });

                    // Event Section 1 Images and Content
                    gsap.from(".event-sec1 .event-img-section, .event-sec2 .event-img-section", {
                        opacity: 0,
                        y: 120,
                        scale: 1.05,
                        duration: 1.4,
                        ease: defaultEase,
                        scrollTrigger: {
                            trigger: ".event-sec1",
                            start: "top 80%",
                            end: "bottom 60%",
                            scroller: ".smooth-scroll",
                            scrub: defaultScrub,
                            toggleActions: "play none none none",
                        },
                    });

                    // Event Section 1 Image Details
                    gsap.from(".event-sec1 .event-img-details", {
                        opacity: 0,
                        x: -120,
                        scale: 1.03,
                        duration: 1.4,
                        delay: 0.3,
                        ease: defaultEase,
                        scrollTrigger: {
                            trigger: ".event-sec1",
                            start: "top 80%",
                            end: "bottom 60%",
                            scroller: ".smooth-scroll",
                            scrub: defaultScrub,
                            toggleActions: "play none none none",
                        },
                    });

                    // Relative Image Animation with Overshoot
                    gsap.from(".event-sec1 .relative-img img", {
                        opacity: 0,
                        scale: 0.95,
                        duration: 1.3,
                        ease: "back.out(1.7)", // Add overshoot effect
                        scrollTrigger: {
                            trigger: ".event-sec1 .relative-img",
                            start: "top 80%",
                            scroller: ".smooth-scroll",
                            scrub: defaultScrub,
                            toggleActions: "play none none none",
                        },
                    });

                    // Event Section 2 Image Details
                    gsap.from(".event-sec2 .event-img-details", {
                        opacity: 0,
                        x: 150,
                        scale: 1.03,
                        duration: 1.4,
                        delay: 0.4,
                        ease: "sine.out",
                        scrollTrigger: {
                            trigger: ".event-sec2",
                            start: "top 80%",
                            end: "bottom 60%",
                            scroller: ".smooth-scroll",
                            scrub: defaultScrub,
                            toggleActions: "play none none none",
                        },
                        onComplete: function () {
                            if (locoScroll && typeof locoScroll.update === 'function') {
                                locoScroll.update();
                            }
                        },
                    });

                    // Absolute Image Animation
                    gsap.from(".event-absolute-img", {
                        opacity: 0,
                        y: 100,
                        scale: 0.92,
                        duration: 1.3,
                        ease: "circ.out",
                        scrollTrigger: {
                            trigger: ".event-absolute-img",
                            start: "top 80%",
                            scroller: ".smooth-scroll",
                            scrub: defaultScrub,
                            toggleActions: "play none none none",
                        },
                        onComplete: function () {
                            if (locoScroll && typeof locoScroll.update === 'function') {
                                locoScroll.update();
                            }
                        },
                    });
                }



                // gsap.from(".swiper-slide .msg-icon-div", {
                //   opacity: 0,
                //   scale: 0.8, // Start smaller
                //   duration: 1.2,
                //   ease: "bounce.out", // Add a bouncy effect
                //   scrollTrigger: {
                //     trigger: ".swiper-slide .msg-icon-div",
                //     start: "top 80%",
                //     scrub: true,
                //     scroller: ".smooth-scroll", // Update this if using a smooth scrolling library
                //     toggleActions: "play none none none",
                //   },
                //   onComplete: function () {
                //     locoScroll.update();  // Call locoScroll update on animation completion
                //   }
                // });

                // // Animate the main image and text in the img-section
                // gsap.from(".swiper-slide .img-section img", {
                //   opacity: 0,
                //   y: 50, // Slide up effect
                //   scale: 0.9, // Slightly zoom in
                //   duration: 1.2,
                //   ease: "power3.out",
                //   scrollTrigger: {
                //     trigger: ".swiper-slide .img-section",
                //     start: "top 80%",
                //     scroller: ".smooth-scroll",
                //     scrub: true,
                //     toggleActions: "play none none none",
                //   },
                //   onComplete: function () {
                //     locoScroll.update();  // Call locoScroll update on animation completion
                //   }
                // });

                // gsap.from(".swiper-slide .img-section p", {
                //   opacity: 0,
                //   y: 30, // Slide up effect for the text
                //   duration: 1,
                //   delay: 0.3, // Staggered entry
                //   ease: "power2.out",
                //   scrollTrigger: {
                //     trigger: ".swiper-slide .img-section",
                //     start: "top 80%",
                //     scroller: ".smooth-scroll",
                //     scrub: true,
                //     toggleActions: "play none none none",
                //   },
                //   onComplete: function () {
                //     locoScroll.update();  // Call locoScroll update on animation completion
                //   }
                // });

                // gsap.from(".swiper-slide .star-div img", {
                //   opacity: 0,
                //   scale: 0.5,
                //   y: -100,
                //   stagger: 0.2,
                //   duration: 0.8,
                //   ease: "bounce.out", // Bouncy effect
                //   scrollTrigger: {
                //     trigger: ".swiper-slide .content-1",
                //     start: "top 80%",
                //     scroller: ".smooth-scroll",
                //     scrub: true,
                //     toggleActions: "play none none none",
                //   },
                //   onComplete: function () {
                //     locoScroll.update();  // Call locoScroll update on animation completion
                //   }
                // });


                // // Animate the first paragraph
                // gsap.from(".swiper-slide .content-1 .para-1", {
                //   opacity: 0,
                //   x: -50, // Slide in from the left
                //   duration: 1.2,
                //   ease: "power3.out",
                //   scrollTrigger: {
                //     trigger: ".swiper-slide .content-1",
                //     start: "top 80%",
                //     scroller: ".smooth-scroll",
                //     scrub: true,
                //     toggleActions: "play none none none",
                //   },
                //   onComplete: function () {
                //     locoScroll.update();  // Call locoScroll update on animation completion
                //   }
                // });

                // // Animate the second paragraph
                // gsap.from(".swiper-slide .content-1 .para-2", {
                //   opacity: 0,
                //   x: 50, // Slide in from the right
                //   duration: 1.2,
                //   delay: 0.5, // Staggered entry after the first paragraph
                //   ease: "power3.out",
                //   scrollTrigger: {
                //     trigger: ".swiper-slide .content-1",
                //     start: "top 80%",
                //     scroller: ".smooth-scroll",
                //     scrub: true,
                //     toggleActions: "play none none none",
                //   },
                //   onComplete: function () {
                //     locoScroll.update();  // Call locoScroll update on animation completion
                //   }
                // });




                // Animation for arrow-div1 (Rotating Effect)
                gsap.to(".arrow-div1 img,.arrow-div2 img,.arrow-div3 img,.arrow-event", {
                    rotation: 360, // Full rotation
                    duration: 2, // 2 seconds for each cycle
                    repeat: -1, // Infinite loop
                    ease: "power1.inOut", // Smooth easing
                });

                // Animation for arrow-div2 (Scaling Effect)
                gsap.to(".arrow-btn img", {
                    x: 5, // Move 10px to the right
                    duration: 1, // Short duration for a quick shake
                    repeat: -1, // Infinite loop
                    yoyo: true, // Return to original position
                    ease: "power1.inOut", // Smooth easing
                });


                gsap.timeline({ repeat: -1, yoyo: true }) // Infinite loop with yoyo
                    .to(".arrow-btn2 img", {
                        y: -5, // Move 5px to the right
                        duration: 1, // Duration for the shake
                        ease: "power1.inOut", // Smooth easing for the movement
                    });


                gsap.to(".para-2 img", {
                    opacity: 0.5, // Dim to 50% opacity
                    duration: 1.5, // 1.5 seconds for each cycle
                    repeat: -1, // Infinite loop
                    yoyo: true, // Return to full opacity
                    ease: "power1.inOut", // Smooth easing
                });


                gsap.to(".call-btn", {
                    duration: 1, // Duration for each cycle
                    repeat: -1, // Infinite loop
                    yoyo: true, // Reverse the glow back to normal
                    ease: "power1.inOut", // Smooth easing for glow transition
                });

                gsap.to(".donate-btn", {
                    duration: 1, // Duration for each cycle
                    repeat: -1, // Infinite loop
                    yoyo: true, // Reverse the glow back to normal
                    ease: "power1.inOut", // Smooth easing for glow transition
                });

                // // Animate curve-card-1
                // gsap.fromTo(".curve-card-1", 
                //   {
                //     opacity: 0, 
                //     x: 100, 
                //     rotation: -30 
                //   }, 
                //   {
                //     opacity: 0.8,
                //     x: 0,
                //     rotation: -15,
                //     duration: 2,
                //     ease: "power2.out",
                //     repeat: -1,
                //     yoyo: true
                //   }
                // );

                // gsap.fromTo(".curve-card-2", 
                //   {
                //     opacity: 0, 
                //     y: -100, 
                //     rotation: -30 
                //   }, 
                //   {
                //     opacity: 0.8,
                //     x: 0,
                //     rotation: -15,
                //     duration: 2,
                //     ease: "power2.out",
                //     repeat: -1,
                //     yoyo: true
                //   }
                // );

                const tl1 = gsap.timeline({ repeat: -1, yoyo: true, repeatDelay: 0 });

                tl1.fromTo(".curve-card-1", { opacity: 0, rotationY: -180, scale: 0.7 }, { opacity: 1, rotationY: 0, scale: 1, duration: 1.2, ease: "power4.out" })
                    .fromTo(".curve-card-1 .para-1", { opacity: 0, scale: 0.3 }, { opacity: 1, scale: 1, duration: 1.2, ease: "power4.out" }, "-=1")
                    .fromTo(".curve-card-2", { opacity: 0, rotationY: 180, scale: 0.7 }, { opacity: 1, rotationY: 0, scale: 1, duration: 1.2, ease: "power4.out" })
                    .fromTo(".curve-card-2 .para-2", { opacity: 0, scale: 0.3 }, { opacity: 1, scale: 1, duration: 1.2, ease: "power4.out" }, "-=1");

                const tl = gsap.timeline({ repeat: -1, yoyo: true, repeatDelay: 0.8 });

                // Animating the first card (card-1)
                tl.from(".card-1 .content-1-1-1 .img-div2", {
                    opacity: 0,
                    y: 30,
                    duration: 0.5, // Reduced duration for faster animation
                    ease: "power2.out" // Fast easing function for smoother speed
                })

                // Animating the fourth card (card-4)
                tl.from(".card-4 .arrow-btn2", {
                    opacity: 0,
                    y: 30,
                    duration: 0.5, // Reduced duration for faster animation
                    ease: "power2.out"  // Faster easing function
                });
                // Create a timeline for the swiper buttons

                // const tl6 = gsap.timeline({ repeat: -1, yoyo: true, repeatDelay: 0.3 });

                // // Slide-in animation for next and prev buttons
                // tl6.from("#nextArrow", {
                //   opacity: 0,
                //   x: 20, // Slide in from the right
                //   duration: 0.6,
                //   ease: "power2.out"
                // })
                //   .from("#prevArrow", {
                //     opacity: 0,
                //     x: -20, // Slide in from the left
                //     duration: 0.6,
                //     ease: "power2.out"
                //   }, "-=0.3");

                // const tl31 = gsap.timeline({ repeat: -1, yoyo: true, repeatDelay: 2 });

                // // Rotate animation for each element without fade-in effect
                // tl31.from(".content-1-4 .para-1 p", {
                //   rotationX: -360,
                //   duration: 0.8,
                //   ease: "power2.out"
                // })
                // .from(".para-1 p", {
                //   rotationX: -360,
                //   duration: 0.8,
                //   ease: "power2.out"
                // }, "-=0.6")
                // .from(".content-1-2 p", {
                //   rotationX: -360,
                //   duration: 0.8,
                //   ease: "power2.out"
                // }, "-=0.5")
                // .from(".para-2 p", {
                //   rotationX: -360,
                //   duration: 0.8,
                //   ease: "power2.out"
                // }, "-=0.4");


                // // Select the image inside the ring-img-section
                // const ringImage = document.querySelector('.ring-img-section img');

                // // GSAP animation: Swing effect like a pendulum
                // gsap.fromTo(ringImage, 
                //   {
                //     opacity: 0,   // Start with opacity 0
                //     rotation: -360, // Start tilted
                //   },
                //   {
                //     duration: 2,  // Duration in seconds
                //     opacity: 1,   // Fade in
                //     rotation: 30, // Swing to the right
                //     yoyo: true,   // Repeats the animation back to the starting position
                //     repeat: -1,   // Infinite repeat
                //     ease: "power1.inOut",  // Smooth easing for swing
                //   }
                // );

                if (window.innerWidth > 768) {
                    gsap.from(".donate-first-sec h1", {
                        opacity: 0,
                        y: -100,
                        scale: 0.9,
                        duration: 1.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".donate-main-section",
                            start: "top 100%",
                            scroller: ".smooth-scroll",
                            scrub: true,
                            toggleActions: "play none none none",
                        },
                    });
                }

                if (window.innerWidth > 768) {
                    gsap.from(".donate-first-sec p", {
                        opacity: 0,
                        x: 100,
                        scale: 0.9,
                        duration: 1.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".donate-main-section",
                            start: "top 100%",
                            scroller: ".smooth-scroll",
                            scrub: true,
                            toggleActions: "play none none none",
                        },
                    });

                    gsap.from(".donate-second-sec", {
                        opacity: 0,
                        x: -100,
                        scale: 0.9,
                        duration: 1.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".donate-main-section",
                            start: "top 100%",
                            scroller: ".smooth-scroll",
                            scrub: true,
                            toggleActions: "play none none none",
                        },
                    });

                }


                const tlDonate = gsap.timeline({ repeat: -1, yoyo: true, repeatDelay: 0 });

                // Smooth entrance for the arrow image
                tlDonate.from(".donate-second-sec img", {
                    scale: .5,
                    opacity: 0,
                    duration: 1.5,
                    ease: "circ.out"
                });

                gsap.fromTo(
                    ".event-absolute-img img, .contact-line-bg img",
                    {
                        scale: 1,
                        x: 0,
                        y: 0
                    },
                    {
                        scale: 1.5,             // Scale to 1.5x (similar to background-size: 150%)
                        // x: "10vw",              // Horizontal shift to simulate background-position change
                        y: "-50vh",             // Vertical shift to simulate background-position change
                        duration: 5,            // Duration of the animation
                        ease: "power1.inOut",   // Smooth easing
                        repeat: -1,             // Infinite loop
                        yoyo: true,             // Reverse the animation back to its original state
                        onComplete: function () {
                            if (locoScroll && typeof locoScroll.update === 'function') {
                                locoScroll.update();
                            }
                        }
                    }
                );


            }





            document.querySelectorAll(".page-menu2,.donate-second-sec,.donate-btn,.footer-section").forEach(function (menu) {
                // For each "page-menu2" element, find all <a> tags and add the click event listener
                menu.querySelectorAll('a').forEach(function (link) {
                    link.addEventListener('click', function (e) {
                        console.log("Clicked link inside page-menu2");
                        e.preventDefault(); // Prevent default link behavior

                        const overlay = document.getElementById("overlay1"); // Get the overlay element
                        const target = this.getAttribute("href"); // Get the href attribute of the link

                        if (target.startsWith('#')) {
                            e.preventDefault(); // Prevent navigation to the section
                            locoScroll.scrollTo(target); // Scroll to the section using locoScroll
                        } else {
                            window.location.href = target; // Navigate to external links
                        }
                    });
                });
            });




            function openNav() {
                document.getElementById("mySidenav").style.width = "305px";
                document.getElementById("menuOverlay").style.display = "block";
            }

            function closeNav() {
                document.getElementById("mySidenav").style.width = "0";
                document.getElementById("menuOverlay").style.display = "none";
            }

            document.querySelectorAll('#page-menu a, #page-menu1 a,#donate2-now a,#donate1-now a,#footer-navigation a').forEach(link => {
                if (window.location.href.includes(link.getAttribute('href'))) {
                    link.classList.add('active');
                }

                link.addEventListener('click', () => {
                    document.querySelectorAll('#page-menu a.active, #page-menu1 a.active,#donate2-now a.active,#donate1-now a.active,#footer-navigation a.active').forEach(item => {
                        item.classList.remove('active');
                    });

                    link.classList.add('active');
                });
            });
            // contact-form
            // function toggleSelection(button) {
            //   // Remove 'btn-selected' class from all buttons
            //   const buttons = document.querySelectorAll('.button-sec .btn');
            //   buttons.forEach(btn => btn.classList.remove('btn-selected'));

            //   // Add 'btn-selected' class to the clicked button
            //   button.classList.add('btn-selected');
            // }

            function toggleSelection(button) {
                // Remove 'btn-selected' class from all buttons
                const buttons = document.querySelectorAll('.button-sec .btn');
                buttons.forEach(btn => btn.classList.remove('btn-selected'));

                // Add 'btn-selected' class to the clicked button
                button.classList.add('btn-selected');

                // Update the hidden input field with the selected program value
                const selectedProgram = button.textContent.trim();
                document.getElementById('selectedProgram').value = selectedProgram;
            }


            // tab



            console.log("swiper", "swiper")
            // Get all tab buttons and contents
            const tabButtons = document.querySelectorAll(".swiper-btn");
            const tabContents = document.querySelectorAll(".tab-content-1");

            // Function to update the active tab and corresponding content
            const updateActiveTab = (realIndex) => {
                // Calculate the normalized index for looped Swiper
                const normalizedIndex = realIndex % tabButtons.length;

                // Remove active classes from all buttons and contents
                tabButtons.forEach((btn) => btn.classList.remove("active"));
                tabContents.forEach((content) => content.classList.add("hidden"));

                // Add active class to the current button and content
                const activeButton = tabButtons[normalizedIndex];
                if (activeButton) activeButton.classList.add("active");

                const tabId = activeButton.getAttribute("data-tab");
                const activeContent = document.getElementById(tabId);
                if (activeContent) activeContent.classList.remove("hidden");
            };

            // Initialize Swiper
            const swiper = new Swiper("#tab-btn", {
                modules: [Navigation], // Make sure to include Navigation module
                loop: true,
                slidesPerView: "auto",
                spaceBetween: 30,
                centeredSlides: true,
                navigation: {
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                },
                breakpoints: {
                    1400: { slidesPerView: 5 },
                    1200: { slidesPerView: 4 },
                    1092: { slidesPerView: 4 },
                    767: { slidesPerView: 3 },
                    501: { slidesPerView: 2 },
                    0: { slidesPerView: 1 },
                },
            });
            console.log("swiper", swiper)

            // Update tabs on Swiper slide change
            swiper.on("slideChange", () => {
                const realIndex = swiper.realIndex; // Get real index
                updateActiveTab(realIndex); // Sync tabs
            });

            // Add click events to tab buttons
            tabButtons.forEach((button, index) => {
                button.addEventListener("click", () => {
                    swiper.slideToLoop(index); // Go to the corresponding slide
                    swiper.loopFix(); // Fix the loop to avoid index errors
                    updateActiveTab(index);   // Sync tab content manually
                });
            });

            // Set the initial active tab
            updateActiveTab(swiper.realIndex);

            const blogSwiper = new Swiper('.blogswipe', {
                modules: [Navigation, Autoplay],
                slidesPerView: 2,
                spaceBetween: 30,
                loop: true,
                navigation: {
                    nextEl: '#nextArrow',
                    prevEl: '#prevArrow',
                },
                autoplay: {
                    delay: 3000,
                    disableOnInteraction: false
                },
                breakpoints: {
                    0: {
                        slidesPerView: 1
                    },
                    1201: {
                        slidesPerView: 2
                    }
                }
            });



            // swiper

            // Function to animate active slide
            function animateActiveSlide(activeSlide) {
                if (!activeSlide) return;

                // // Animate the message icon
                // gsap.fromTo(
                //   activeSlide.querySelector('.msg-icon-div'),
                //   { opacity: 0, scale: 0.8 }, // Initial state
                //   { opacity: 1, scale: 1, duration: 1.2, ease: "bounce.out" } // Final state
                // );

                // // Animate the main image in the img-section
                // gsap.fromTo(
                //   activeSlide.querySelector('.img-section img'),
                //   { opacity: 0, y: 50, scale: 0.9 },
                //   { opacity: 1, y: 0, scale: 1, duration: 1.2, ease: "power3.out" }
                // );

                // // Animate the text in the img-section
                // gsap.fromTo(
                //   activeSlide.querySelector('.img-section p'),
                //   { opacity: 0, y: 30 },
                //   { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
                // );

                // // Animate the stars
                // gsap.fromTo(
                //   activeSlide.querySelectorAll('.star-div img'),
                //   { opacity: 0, scale: 0.5, y: -100 },
                //   { opacity: 1, scale: 1, y: 0, stagger: 0.2, duration: 0.8, ease: "bounce.out" }
                // );

                // // Animate the first paragraph
                // gsap.fromTo(
                //   activeSlide.querySelector('.content-1 .para-1'),
                //   { opacity: 0, x: -50 },
                //   { opacity: 1, x: 0, duration: 1.2, ease: "power3.out" }
                // );

                // // Animate the second paragraph
                // gsap.fromTo(
                //   activeSlide.querySelector('.content-1 .para-2'),
                //   { opacity: 0, x: 50 },
                //   { opacity: 1, x: 0, duration: 1.2, ease: "power3.out" }
                // );
            }

            document.addEventListener("DOMContentLoaded", function () {
                console.log("$('.owl-carousel')", $('.owl-carousel'))
                const owl = $('.owl-carousel').owlCarousel({
                    loop: true,
                    margin: 10,
                    nav: false,
                    dots: false,
                    autoplay: false,
                    autoplayHoverPause: true,
                    items: 2,
                    responsive: {
                        0: { items: 1 },
                        991: { items: 1 },
                        1000: { items: 2 }
                    }
                });

                $('#prevArrow').click(function () {
                    owl.trigger('prev.owl.carousel');
                });

                $('#nextArrow').click(function () {
                    owl.trigger('next.owl.carousel');
                });

                if (locoScroll && typeof locoScroll.update === 'function') {
                    locoScroll.update();
                }
            });


            const section5Swiper = new Swiper('#section-5-swiper', {
                modules: [Autoplay, Navigation],
                slidesPerView: 'auto',
                spaceBetween: 24,
                centeredSlides: true,
                loop: true,
                speed: 700,
                grabCursor: true,
                allowTouchMove: true,
                autoplay: {
                    delay: 3000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                },
                slideToClickedSlide: true,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                on: {
                    slideChange: function () {
                        const activeSlide = this.slides[this.activeIndex];
                        animateActiveSlide(activeSlide);
                    }
                },
                breakpoints: {
                    0: {
                        spaceBetween: 12,
                        slidesPerView: 1,
                        centeredSlides: true,
                    },
                    768: {
                        spaceBetween: 16,
                        slidesPerView: 'auto',
                        centeredSlides: true,
                    },
                    1080: {
                        spaceBetween: 24,
                        slidesPerView: 'auto',
                        centeredSlides: true,
                    },
                },
            });






            function loadLogoAnimation() {
                const flip = Flip.fit("#headerLogo", "#heroLogo", {
                    scale: true,
                    duration: 1,
                    ease: "none"
                });
                flip.pause(1);


                ScrollTrigger.create({
                    trigger: ".header",
                    scroller: "[data-scroll-container]",
                    start: "top top",
                    end: "top top",
                    endTrigger: "#heroLogo",
                    markers: false,
                    onUpdate: (s) => {
                    flip.progress(1 - s.progress);
                    }
                });

                const headerTween = gsap
                    .to(".header", {
                    opacity: 0,                // Fade out the header (opacity goes to 0)
                    yPercent: -100,            // Move the header out of view vertically (yPercent -100)
                    duration: 5,               // Duration of the fade and slide (5 seconds for a slow effect)
                    ease: "power1.inOut",      // Smooth easing for both fade and move
                    paused: true,              // Start in a paused state
                    reverse: true
                    })
                    .reverse();

                ScrollTrigger.create({
                    trigger: "#heroLogo",
                    scroller: "[data-scroll-container]",
                    start: "top top",
                    end: "+=" + document.body.clientHeight,
                    markers: false,
                    onUpdate: (s) => {
                        // Ensure the header is only shown when scrolled to the top
                        if (s.progress === 0 && s.direction < 0) {
                            headerTween.reverse(); // Show header
                        } else if (s.direction > 0) {
                            headerTween.play(); // Hide header
                        }
                    }
                });

                const scg = document.querySelectorAll('#headerLogo');
                const paths = document.querySelectorAll('#headerLogo path');
                gsap.set(paths, {
                    opacity: 0,           // Initially set opacity to 0
                    scale: 0,             // Initially scale them down to 0
                    rotation: () => Math.random() * 360, // Random initial rotations
                    x: () => Math.random() * (window.innerWidth / 2), // Random X off-screen
                    y: () => Math.random() * (window.innerHeight / 2), // Random Y off-screen
                });

                // Animate paths to chase towards the center with a spiral rotation
                gsap.set(paths, {
                    opacity: 0,           // Initially set opacity to 0
                    scale: 0.5,           // Initially scale them down a bit
                    rotation: () => Math.random() * 10, // Random slight rotation
                    x: () => Math.random() > 0.5 ? -200 : window.innerWidth + 200, // Start from the left or right
                    y: () => Math.random() > 0.5 ? -200 : window.innerHeight + 200, // Start from the top or bottom
                });

                // Animate paths smoothly gathering towards the center with light rotation
                gsap.to(paths, {
                    duration: function (index) {
                        if (index < 3) return 3; // Normal duration for the first 4 paths
                        if (index < 27) return 3; // Faster duration for the next 23 paths
                        return 3; // Normal duration for the remaining paths
                    },
                    opacity: 1,
                    scale: 1,
                    x: 0,
                    y: 0,
                    rotation: 360,
                    ease: "power2.out",
                    stagger: function (index) {
                        if (index < 4) return 0.2; // Normal stagger for the first 4 paths
                        if (index < 27) return 0.1; // Faster stagger for the next 23 paths
                        return 0.2; // Normal stagger for the remaining paths
                    },
                    onComplete: function () {
                        gsap.to(scg, {
                            duration: 2,
                            rotation: "+=1",
                            repeat: -1,
                            yoyo: true,
                            ease: "power2.inOut",
                        });

                    }
                });

            }


            document.querySelectorAll('.swiper-btn').forEach(button => {
                button.addEventListener('click', () => {
                    // Get the tab ID from the button's data-tab attribute
                    const tabId = button.getAttribute('data-tab');
                    const tabContent = document.getElementById(tabId);

                    // Hide all tab content and show the selected one
                    document.querySelectorAll('.tab-content-1').forEach(content => {
                        content.style.display = 'none';
                    });
                    tabContent.style.display = 'block';

                    // Select images inside the .content-1-3 div of the active tab
                    const images = tabContent.querySelectorAll('.content-1-1 img');

                    // Animate images with GSAP
                    gsap.from(images, {
                        duration: 1,
                        opacity: 0,
                        stagger: 0.3, // Delay between animations
                        ease: "elastic.out(1, 0.5)", // Bounce effect on arrival
                    });
                });
            });

            $.validator.addMethod(
                "regex",
                function (value, element, regexp) {
                    return this.optional(element) || regexp.test(value);
                },
                "Please check your input."
            );

            $.validator.addMethod(
                "mobileByCountry",
                function (value, element) {
                    if (this.optional(element)) return true;

                    const $country = $("#countryCode");
                    const min = parseInt($country.attr("data-min"), 10);
                    const max = parseInt($country.attr("data-max"), 10);
                    const patternAttr = $country.attr("data-pattern");

                    if (!value || !/^\d+$/.test(value)) return false;
                    if (Number.isFinite(min) && value.length < min) return false;
                    if (Number.isFinite(max) && value.length > max) return false;
                    if (!patternAttr) return true;

                    try {
                        return new RegExp(patternAttr).test(value);
                    } catch (err) {
                        return false;
                    }
                },
                function () {
                    return $("#countryCode").attr("data-message") || "Please enter a valid mobile number for the selected country.";
                }
            );

            if ($("#contactForm").data("validator")) {
                $("#contactForm").removeData("validator").off(".validate");
                $("#contactForm").find("em.error, label.error").remove();
            }

            $("#contactForm").validate({
                ignore: [],
                rules: {
                    name: {
                        required: true,
                        regex: /^[a-zA-Z][a-zA-Z\s]*$/
                    },
                    contactEmail: {
                        required: true,
                        email: true,
                        regex: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                    },
                    countryCode: {
                        required: true
                    },
                    mobile: {
                        required: true,
                        mobileByCountry: true
                    },
                    company: {
                        required: true
                    },
                    message: {
                        required: true
                    }
                },
                messages: {
                    name: {
                        required: "Name is required",
                        regex: "Name should contain only letters and spaces."
                    },
                    contactEmail: {
                        required: "Email is required",
                        email: "Please enter a valid email address",
                        regex: "Please enter a valid email address"
                    },
                    countryCode: {
                        required: "Country code is required"
                    },
                    mobile: {
                        required: "Mobile number is required"
                    },
                    company: {
                        required: "Company is required"
                    },
                    message: {
                        required: "Message is required"
                    }
                },
                errorElement: "em",
                errorClass: "error",
                validClass: "is-valid",
                errorPlacement: function (error, element) {
                    const name = element.attr("name");
                    error.addClass("invalid-feedback");
                    $(`em.error[id='${name}-error'], label.error[id='${name}-error']`).not(error).remove();

                    if (name === "mobile" || name === "countryCode") {
                        $("#mobile-error-container").empty().append(error);
                    } else {
                        error.insertAfter(element);
                    }
                    $(".form-div").css("height", "auto");
                    window?.locoScroll?.update?.();
                },
                success: function (label, element) {
                    $(label).remove();
                    window?.locoScroll?.update?.();
                },
                highlight: function (element) {
                    $(element).addClass("is-invalid").removeClass("is-valid");
                    if (element.name === "mobile" || element.name === "countryCode") {
                        $("#mobile-input-group").addClass("is-invalid").removeClass("is-valid");
                    }
                    window?.locoScroll?.update?.();
                },
                unhighlight: function (element) {
                    $(element).addClass("is-valid").removeClass("is-invalid");
                    if (element.name === "mobile" || element.name === "countryCode") {
                        if (!$("#mobile").hasClass("is-invalid") && !$("#countryCode").hasClass("is-invalid")) {
                            $("#mobile-input-group").addClass("is-valid").removeClass("is-invalid");
                        }
                    }
                    window?.locoScroll?.update?.();
                },
                submitHandler: function (form) {
                       const captchaResponse = window.grecaptcha.getResponse();

    if (captchaResponse.length === 0) {
        // 2. If not completed, show the error message
        $("#captcha-error").show();
        
        // Update scroll because showing error changes container height
        window?.locoScroll?.update?.();
        return false; // Prevent submission
    } else {
        // 3. If completed, hide error and proceed
        $("#captcha-error").hide();
        writefunction();
    }
                }
            });
            $("#emailForm").validate({
                rules: {
                    email: {
                        required: true,
                        email: true,
                        regex: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                    }
                },
                messages: {
                    email: {
                        required: "Email address is required",
                        email: "Please enter a valid email address",
                        regex: "Invalid email format"
                    }
                },
                errorElement: "em",
                errorPlacement: function (error, element) {
                    error.addClass("invalid-feedback");
                    error.insertAfter(element);

                },
                highlight: function (element) {
                    $(element).addClass("is-invalid").removeClass("is-valid");
                },
                unhighlight: function (element) {
                    $(element).addClass("is-valid").removeClass("is-invalid");
                },
                submitHandler: function (form) {
                    writefunctionEmail();
                }
            });
            function writefunction() {
                displayLoading();

                const name = $("#name").val();
                const email = $("#contactEmail").val();
                const countryCode = $("#countryCode").attr("data-code") || "";
                const mobileNumber = $("#mobile").val();
                const mobile = `${countryCode}${mobileNumber}`;
                const company = $("#company").val();
                const message = $("#message").val();
                const website = 'SAIHATASK';
                const selectedProgram = $("#selectedProgram").val();
                    

                const data = {
                    Name: name,
                    Email: email,
                    Mobile: mobile,
                    Company: company,
                    Message: message,
                    Website: website,
                    SelectedProgram: selectedProgram,
                };

                $.ajax({
                    url: "https://script.google.com/macros/s/AKfycbwZZ12DnRzWujK4HnxY4MsQz0EIT6KEPd9pmZdjD3f-zAfR-f6gZFqTbkFxzU2Zx_jJkw/exec",
                    data: data,
                    method: "POST",
                    success: function () {
                        stopLoading();
                        swal("Contacted Successfully!", `Thank you ${capitalizeFirstLetter(name)}. We appreciate your interest.`, "success");
                        $("#contactForm")[0].reset();
                        $("#selectedProgram").val("Demo Request");
                        $(".button-sec .btn").removeClass("btn-selected");
                        $(".button-sec .btn:first").addClass("btn-selected");
                        window.dispatchEvent(new CustomEvent("contactFormReset"));
                        $("#mobile-input-group").removeClass("is-valid is-invalid");
                        $("#mobile-error-container").empty();
                    },
                    error: function () {
                        swal("Error", "Error contacting. Please try again.", "error");
                    },
                });
            }
            function writefunctionEmail() {
                displayLoading();

                const email = $("#email").val();

                const data = {
                    Email: email
                };

                $.ajax({
                    url: "https://script.google.com/macros/s/AKfycbwZZ12DnRzWujK4HnxY4MsQz0EIT6KEPd9pmZdjD3f-zAfR-f6gZFqTbkFxzU2Zx_jJkw/exec",
                    data: data,
                    method: "POST",
                    success: function () {
                        stopLoading();
                        swal("Successfully Subscribed to Our Newsletter", `Thank you ${(email)}. We appreciate your interest.`, "success");
                        $("#emailForm")[0].reset();
                        $("#selectedProgram").val("Demo Request");
                        $(".button-sec .btn").removeClass("btn-selected");
                        $(".button-sec .btn:first").addClass("btn-selected");
                    },
                    error: function () {
                        swal("Error", "Error contacting. Please try again.", "error");
                    },
                });
            }
            function capitalizeFirstLetter(string) {
                return string.charAt(0).toUpperCase() + string.slice(1);
            }

            function displayLoading() {
                const loader = document.querySelector("#loading2");
                if (loader) loader.style.visibility = "visible";
            }

            function stopLoading() {
                const loader = document.querySelector("#loading2");
                if (loader) loader.style.visibility = "hidden";
            }

            ScrollTrigger.addEventListener("refresh", () => {
                if (locoScroll && typeof locoScroll.update === 'function') {
                    locoScroll.update();
                }
            });
            ScrollTrigger.refresh();

            return null; // This component just runs the effect
        };

    }, []);

};

export default Preloader;
